import { useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { LifeBuoy, TriangleAlert, ImagePlus, X } from "lucide-react";

function resizeImage(file, maxSize = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        if (width >= height) { height = Math.round((height * maxSize) / width); width = maxSize; }
        else { width = Math.round((width * maxSize) / height); height = maxSize; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((b) => b ? resolve(b) : reject(new Error("fail")), "image/jpeg", quality);
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function FaultReportButton({ compact = false }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [emailMe, setEmailMe] = useState(true);
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image."); return; }
    try { const b = await resizeImage(file); setPhoto(b); setPreview(URL.createObjectURL(b)); }
    catch { toast.error("Could not process image."); }
  };

  const reset = () => { setSubject(""); setMessage(""); setEmailMe(true); setPhoto(null); setPreview(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) { toast.error("Add a subject and a description."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/fault-reports", { subject, message, email_me: emailMe });
      if (photo && data?.id) {
        const fd = new FormData();
        fd.append("file", photo, "screenshot.jpg");
        try { await api.post(`/fault-reports/${data.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
        catch { /* report still saved without screenshot */ }
      }
      toast.success("Fault reported — thanks! Our team has been notified.");
      setOpen(false); reset();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send report");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {compact ? (
          <button className="text-[#94A3B8] hover:text-[#FFB800] transition-colors" title="Report a fault" data-testid="report-fault-btn-mobile">
            <LifeBuoy className="w-5 h-5" />
          </button>
        ) : (
          <button className="btn-ghost w-full h-11 font-head text-sm flex items-center justify-center gap-2 mb-2" data-testid="report-fault-btn">
            <LifeBuoy className="w-4 h-4" /> Report a Fault
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-[#1A1C23] border border-white/10 text-white" data-testid="report-fault-dialog">
        <DialogHeader>
          <DialogTitle className="font-head uppercase text-2xl flex items-center gap-2"><TriangleAlert className="w-6 h-6 text-[#FFB800]" /> Report a Fault</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="report-fault-form">
          <p className="font-body text-sm text-[#94A3B8]">Found a bug or something not working? Tell us and our team will look into it.</p>
          <div><label className="il-label">Subject *</label><input className="il-input" value={subject} onChange={(e) => setSubject(e.target.value)} required placeholder="e.g. QR code won't scan" data-testid="fault-subject" /></div>
          <div><label className="il-label">What&apos;s happening? *</label><textarea className="il-input py-3" style={{ height: "auto", minHeight: "6rem" }} value={message} onChange={(e) => setMessage(e.target.value)} required placeholder="Describe the fault and what you were doing when it happened." data-testid="fault-message" /></div>
          <div>
            <label className="il-label">Screenshot (optional)</label>
            {preview ? (
              <div className="relative w-40">
                <img src={preview} alt="Screenshot" className="w-40 h-28 object-cover border border-white/10" data-testid="fault-photo-preview" />
                <button type="button" onClick={() => { setPhoto(null); setPreview(null); }} className="absolute top-1 right-1 bg-[#0D0E11]/80 border border-white/20 p-1 hover:bg-[#FF3B30]/80 transition-colors"><X className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()} className="w-40 h-28 border-2 border-dashed border-white/15 flex flex-col items-center justify-center gap-1 text-[#94A3B8] hover:border-[#FFB800]/50 hover:text-[#FFB800] transition-colors" data-testid="fault-photo-btn"><ImagePlus className="w-6 h-6" /><span className="font-body text-[11px]">Add screenshot</span></button>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={pickPhoto} className="hidden" data-testid="fault-photo-input" />
          </div>
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input type="checkbox" checked={emailMe} onChange={(e) => setEmailMe(e.target.checked)} className="w-4 h-4 accent-[#FFB800]" data-testid="fault-email-me" />
            <span className="font-body text-sm text-[#94A3B8]">Email me a copy</span>
          </label>
          <DialogFooter><button className="btn-signal w-full h-12 font-head" disabled={busy} data-testid="fault-submit">{busy ? "Sending…" : "Send Report"}</button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
